import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function DisputeResolutionClause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            Plain-English Explanation of Dispute Resolution Clause
          </h1>

          <p className="text-base md:text-lg leading-relaxed mb-12" style={{ color: colors.textMuted }}>
            A dispute resolution clause specifies how disagreements will be handled, often requiring mediation or arbitration before going to court.
          </p>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              This clause establishes a process for resolving conflicts without immediate litigation. It may require negotiation, mediation, or binding arbitration as steps before court action. The goal is faster, cheaper resolution than traditional lawsuits.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Why This Clause Exists</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Litigation is expensive, slow, and public. Dispute resolution clauses encourage parties to resolve issues collaboratively. They reduce legal costs, maintain confidentiality, and often produce faster outcomes than court proceedings. Many businesses prefer predictable arbitration over uncertain jury trials.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
            <ul className="space-y-3 ml-6">
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                You may be required to attempt mediation before suing
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Arbitration decisions are usually final with limited appeal rights
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Can be faster and cheaper than court litigation
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Proceedings are typically private and confidential
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>What to Watch Out For</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Check whether arbitration is binding, who pays arbitrator fees, where proceedings occur, and whether you're waiving rights to jury trial or class actions. Some clauses heavily favor one party through choice of arbitrator or venue.
            </p>
          </section>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Some jurisdictions limit mandatory arbitration clauses, especially in consumer or employment contexts. Courts may refuse to enforce unfair dispute resolution terms. This explanation provides general information but doesn't constitute legal advice about your specific situation.
            </p>
          </section>

          <section className="mb-12">
            <div className="flex gap-4 mb-8">
              <Link to="/clauses" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                ← All Clauses
              </Link>
              <Link to="/clauses/business" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                Business Clauses
              </Link>
            </div>
            <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/explain-governing-law-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Governing Law</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Learn about jurisdiction choice</p>
              </Link>
              <Link to="/explain-limitation-of-liability-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Limitation of Liability</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Understand liability caps</p>
              </Link>
            </div>
          </section>

          <div className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
            <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
            <Link to="/" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
              Try ClauseClear
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Sponsorship Placeholder */}
          <SponsorPlaceholder variant="full" />
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
